/**
 * Шаблоны SendGrid — то, что общее у всех экранов, где сотрудник выбирает
 * динамический шаблон и смотрит его переменные: «Wstrzymanie dostępu»
 * (ТЗ 0074) и «Правила писем» дофинансирования (ТЗ 0085).
 *
 * Вынесено из `lmsBlockSettings`, когда шаблон понадобился второму разделу:
 * второй экземпляр поиска по шаблонам разошёлся бы с первым на первой правке.
 * `lmsBlockSettings` реэкспортирует всё это под прежними именами.
 *
 * Ни React, ни Apollo, ни i18n.
 */

export interface ITemplateOption {
  id: string;
  name: string;
  subject?: string | null;
  /** A saved template the SendGrid list does not contain (or did not load). */
  notInList?: boolean;
}

/**
 * The SendGrid list plus every saved template missing from it — so a saved
 * choice is still shown by name when SendGrid is down or the template was
 * deleted there.
 */
export const templateOptions = (
  templates: ReadonlyArray<{
    id: string;
    name: string;
    subject?: string | null;
  }>,
  saved: ReadonlyArray<{ id?: string | null; name?: string | null }>,
): ITemplateOption[] => {
  const options: ITemplateOption[] = templates.map((tpl) => ({
    id: tpl.id,
    name: tpl.name || tpl.id,
    subject: tpl.subject,
  }));
  saved.forEach(({ id, name }) => {
    if (!id || options.some((o) => o.id === id)) return;
    options.push({ id, name: name || id, notInList: true });
  });
  return options;
};

/** Picker search: name, id and subject, case-insensitive. */
export const templateMatches = (
  option: ITemplateOption | undefined,
  query: string,
): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  if (!option) return false;
  return [option.name, option.id, option.subject || ''].some((part) =>
    part.toLowerCase().includes(needle),
  );
};

/**
 * Шаблон в редакторе SendGrid. id кодируется: он приходит с сервера и
 * попадает в адрес, а адрес собирается строкой.
 */
export const sendgridTemplateUrl = (id: string): string =>
  `https://mc.sendgrid.com/dynamic-templates/${encodeURIComponent(id)}`;

/** Polish interface → the Polish description; anything else → Russian. */
export const variableDescription = (
  variable: { descriptionPl: string; descriptionRu: string },
  language: string | null | undefined,
): string =>
  (language || '').toLowerCase().startsWith('pl')
    ? variable.descriptionPl
    : variable.descriptionRu;

/** `first_name` → `{{first_name}}` (what the template author types). */
export const variableToken = (name: string): string => `{{${name}}}`;

/**
 * graphene `JSONString` — a JSON **string** on the wire (possibly holding
 * JSON once more), or an already-parsed value. Unparseable text is returned
 * as the text itself.
 */
export const parseJsonValue = (raw: unknown): unknown => {
  if (typeof raw !== 'string') return raw;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') {
      const inner = parsed.trim();
      if (inner.startsWith('{') || inner.startsWith('[')) {
        return parseJsonValue(parsed);
      }
    }
    return parsed;
  } catch {
    return raw;
  }
};
